import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { ConnectGatedActions, ConnectModals } from 'features/Connect/utils/connectEnums';
import { ChangeEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { School } from 'types/connectTypes';
import { User } from 'types';
import { Screen, Screens, GatedActionResponse } from './types';
import { History } from 'history';
import ExpressInterestAPI from 'features/Connect/api/expressInterestAPI';
import { connectRedirectUrls, connectUrlParams } from 'utils/constants/urls';
import { CONNECT_JOBBOARD_STATES } from 'utils/constants/connect';
import { buildUrl } from 'utils/util';
import {
  MAX_FILE_NAME_CHARACTER_LENGTH,
  MAX_FILE_SIZE_IN_BYTES,
} from 'features/Connect/utils/connectConstants';
import GeolocationAPI from 'features/Connect/api/geolocationAPI';
import { Option } from 'sharedComponents/Select/types';
import { userTypeToId } from 'utils/typedEnums';
import { Box, Link, Typography } from '@mui/material';
import { ConnectProfileFlowDataTestIds } from 'data-testids/ConnectDataTestIds';
import { nimbleTheme } from 'theme';
import { useConnectContext } from '../Context/ConnectContextProvider';
import { ConnectContextActionsInterface } from '../Context/types';

export const MISSOURI_STATE_ID = 25;

export const NO_FILE_SELECTED_ERROR_MESSAGE = 'Please select a file to upload';

export function checkFileNameAndSize(event: ChangeEvent<HTMLInputElement>): {
  fileData: File;
  error: string;
} {
  const fileData = event.target.files[0];
  let error;

  if (typeof fileData === 'undefined' || fileData.size === 0) {
    error = 'Upload is empty. Please try again.';
  }
  if (fileData.size > MAX_FILE_SIZE_IN_BYTES) {
    error = 'File size must be smaller than 5 MB. Please try a different file.';
  }
  if (fileData.name.length > 215) {
    error = `Please ensure your file name is fewer than ${MAX_FILE_NAME_CHARACTER_LENGTH} characters`;
  }

  return { fileData, error };
}

export const formatResumeFileName = (resumeFilePath: string): string => {
  if (!resumeFilePath) {
    return '';
  } else {
    const path = resumeFilePath.split('?')[0];
    const pathArr = path.split('/');
    return pathArr[pathArr.length - 1].substring(14);
  }
};

export function moreInfoOnClickHandler(
  school: School,
  connectContextActions: ConnectContextActionsInterface,
  vacancyId: string
): void {
  if (vacancyId !== '0') {
    connectContextActions.setSelectedVacancyId(vacancyId);
  }
  connectContextActions.setSelectedSchool(school);
  connectContextActions.setIsMoreInfoSliderOpen(true);
}

export function createButtonUrl({
  basePath,
  schoolId,
  action,
  jobboardState,
}: {
  basePath: string;
  schoolId?: string;
  action?: string;
  jobboardState?: string;
}): string {
  let url = basePath;
  const queryParams = [];

  if (jobboardState) {
    queryParams.push(`${connectUrlParams.REDIRECT}=${jobboardState}`);
  }

  if (schoolId && action) {
    queryParams.push(`school=${schoolId}&action=${action}`);
  }

  if (queryParams.length) {
    url += `?${queryParams.join('&')}`;
  }

  return url;
}

export const getProfileFlowPage = (pathname: string, flowScreens: Screens): Screen => {
  const segments = pathname.split('/');
  const pageSegment = segments[segments.length - 1];
  return flowScreens[pageSegment];
};

const getSearchParams = (searchParams) => {
  return {
    gatedAction: searchParams.get('action')?.toLowerCase(),
    schoolId: searchParams.get('school'),
    jobboardState: searchParams.get(connectUrlParams.REDIRECT),
  };
};

const getContinueToUrl = (jobboardState, gatedAction, schoolId) => {
  const stateConfig = CONNECT_JOBBOARD_STATES[jobboardState?.toUpperCase()];
  if (stateConfig?.shouldRedirectToLandingPage && stateConfig?.landingPageUrl) {
    return stateConfig.landingPageUrl;
  }
  return createButtonUrl({
    basePath: `${connectRedirectUrls.RETURN_TO}/${jobboardState}`,
    action: gatedAction,
    schoolId: schoolId,
  });
};

export const getGatedActionDisplayInfo = (
  hasConditionalRequirements = false
): GatedActionResponse => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { search, pathname } = useLocation();
  const searchParams = new URLSearchParams(search);
  const { gatedAction, schoolId, jobboardState } = getSearchParams(searchParams);
  const returnTo = searchParams.get(connectUrlParams.RETURN_TO);

  let flowDisplayInformation;
  const isCandidateRedirect = returnTo?.includes('/candidate') || pathname.includes('/candidate');

  switch (gatedAction) {
    case ConnectGatedActions.EXPRESS_INTEREST_WAITLIST:
      // Same flow as EXPRESS_INTEREST for now
      flowDisplayInformation = {
        stepper: {
          steps: ['Profile', 'Qualifications', 'Preferences'],
          mobileSteps: ['Profile', 'Qualifications', 'Preferences'],
        },
        alreadyLoggedInRedirect: createButtonUrl({
          basePath: isCandidateRedirect ? returnTo : connectRedirectUrls.RETURN_TO,
          action: gatedAction,
          schoolId: schoolId,
          jobboardState: jobboardState,
        }),
        screens: {
          login: {
            step: null,
            headerText: 'Log in to your account',
            continueToUrl: createButtonUrl({
              basePath: isCandidateRedirect ? returnTo : connectRedirectUrls.PROFILE,
              action: gatedAction,
              schoolId: schoolId,
              jobboardState: jobboardState,
            }),
            backUrl: null,
            primaryButtonLabel: 'Continue',
            primaryButtonLabelMobile: 'Continue',
            requiredFields: ['email'],
          },
          signup: {
            step: null,
            headerText: 'Create Profile',
            subHeadingText:
              'Create your Nimble account to start connecting with principals in your area.',
            continueToUrl: createButtonUrl({
              basePath: connectRedirectUrls.PROFILE,
              action: gatedAction,
              schoolId: schoolId,
              jobboardState: jobboardState,
            }),
            backUrl: null,
            primaryButtonLabel: 'Continue',
            primaryButtonLabelMobile: 'Continue',
            requiredFields: ['email'],
          },
          profile: {
            step: 0,
            headerText: null,
            continueToUrl: createButtonUrl({
              basePath: connectRedirectUrls.QUALIFICATIONS,
              action: gatedAction,
              schoolId: schoolId,
              jobboardState: jobboardState,
            }),
            backUrl: null,
            primaryButtonLabel: 'Continue',
            primaryButtonLabelMobile: 'Continue',
            requiredFields: ['firstName', 'lastName', 'openToContact'],
          },
          qualifications: {
            step: 1,
            headerText: 'Tell us your qualifications',
            continueToUrl: createButtonUrl({
              basePath: connectRedirectUrls.PREFERENCES,
              action: gatedAction,
              schoolId: schoolId,
              jobboardState: jobboardState,
            }),
            backUrl: createButtonUrl({
              basePath: connectRedirectUrls.PROFILE,
              action: gatedAction,
              schoolId: schoolId,
              jobboardState: jobboardState,
            }),
            primaryButtonLabel: 'Continue',
            primaryButtonLabelMobile: 'Continue',
            requiredFields: [
              'resume',
              'certifications',
              'yearsOfTeachingExperience',
              'isEligibleToWorkInUS',
            ],
          },
          preferences: {
            step: 2,
            headerText: 'Your Preferences',
            continueToUrl: getContinueToUrl(jobboardState, gatedAction, schoolId),
            backUrl: createButtonUrl({
              basePath: connectRedirectUrls.QUALIFICATIONS,
              action: gatedAction,
              schoolId: schoolId,
              jobboardState: jobboardState,
            }),
            primaryButtonLabel: 'Save & Finish',
            primaryButtonLabelMobile: 'Finish',
            requiredFields: hasConditionalRequirements ? ['subject', 'grade', 'distance'] : [],
          },
        },
      };
      break;
    default:
      flowDisplayInformation = {
        stepper: {
          steps: ['Profile', 'Qualifications', 'Preferences'],
          mobileSteps: ['Profile', 'Qualifications', 'Preferences'],
        },
        alreadyLoggedInRedirect: createButtonUrl({
          basePath: isCandidateRedirect ? returnTo : connectRedirectUrls.RETURN_TO,
          action: gatedAction,
          schoolId: schoolId,
          jobboardState: jobboardState,
        }),
        screens: {
          login: {
            step: null,
            headerText: 'Log in to your account',
            continueToUrl: createButtonUrl({
              basePath: isCandidateRedirect ? returnTo : connectRedirectUrls.PROFILE,
              action: gatedAction,
              schoolId: schoolId,
              jobboardState: jobboardState,
            }),
            backUrl: null,
            primaryButtonLabel: 'Continue',
            primaryButtonLabelMobile: 'Continue',
            requiredFields: ['email'],
          },
          signup: {
            step: null,
            headerText: 'Create Profile',
            subHeadingText:
              'Create your Nimble account to start connecting with principals in your area.',
            continueToUrl: createButtonUrl({
              basePath: connectRedirectUrls.PROFILE,
              action: gatedAction,
              schoolId: schoolId,
              jobboardState: jobboardState,
            }),
            backUrl: null,
            primaryButtonLabel: 'Continue',
            primaryButtonLabelMobile: 'Continue',
            requiredFields: ['email'],
          },
          profile: {
            step: 0,
            headerText: null,
            continueToUrl: createButtonUrl({
              basePath: connectRedirectUrls.QUALIFICATIONS,
              action: gatedAction,
              schoolId: schoolId,
              jobboardState: jobboardState,
            }),
            backUrl: null,
            primaryButtonLabel: 'Continue',
            primaryButtonLabelMobile: 'Continue',
            requiredFields: ['firstName', 'lastName', 'openToContact'],
          },
          qualifications: {
            step: 1,
            headerText: 'Tell us your qualifications',
            continueToUrl: createButtonUrl({
              basePath: connectRedirectUrls.PREFERENCES,
              action: gatedAction,
              schoolId: schoolId,
              jobboardState: jobboardState,
            }),
            backUrl: createButtonUrl({
              basePath: connectRedirectUrls.PROFILE,
              action: gatedAction,
              schoolId: schoolId,
              jobboardState: jobboardState,
            }),
            primaryButtonLabel: 'Continue',
            primaryButtonLabelMobile: 'Continue',
            requiredFields: [
              'resume',
              'certifications',
              'yearsOfTeachingExperience',
              'isEligibleToWorkInUS',
            ],
          },
          preferences: {
            step: 2,
            headerText: 'Your Preferences',
            continueToUrl: getContinueToUrl(jobboardState, gatedAction, schoolId),
            backUrl: createButtonUrl({
              basePath: connectRedirectUrls.QUALIFICATIONS,
              action: gatedAction,
              schoolId: schoolId,
              jobboardState: jobboardState,
            }),
            primaryButtonLabel: 'Save & Finish',
            primaryButtonLabelMobile: 'Finish',
            requiredFields: hasConditionalRequirements ? ['subject', 'grade', 'distance'] : [],
          },
        },
      };
      break;
  }

  return {
    flowDisplayInformation: flowDisplayInformation,
    pageDisplayInformation: getProfileFlowPage(pathname, flowDisplayInformation.screens),
  };
};

export const updateConnectFirstProfileSetupDateTime = async (user, signupState) => {
  if (!user?.profile?.connect_first_profile_setup && !user?.is_hijacked) {
    const currentTime = new Date(moment.now()).toISOString();
    await ExpressInterestAPI.updateConnectFirstProfileSetupDateTime(
      user.id,
      signupState,
      currentTime
    );
  }
};

export const buildRedirectUrl = (
  path: string,
  jobboardState: string,
  action: ConnectGatedActions,
  school?: School
) => {
  const urlParams: Record<string, string> = {
    [connectUrlParams.REDIRECT]: CONNECT_JOBBOARD_STATES[jobboardState]?.urlParam,
    [connectUrlParams.ACTION]: action,
  };

  if (school) urlParams[connectUrlParams.SCHOOL] = school.nces_id;

  return buildUrl(path, urlParams);
};

export const useConnectActions = () => {
  const { connectContextActions } = useConnectContext();
  const history = useHistory();

  const redirectTo = (
    path: string,
    jobboardState: string,
    action: ConnectGatedActions,
    school?: School
  ) => {
    history.push(buildRedirectUrl(path, jobboardState, action, school));
  };

  const handleConnectUserAction = async (
    user: User | null,
    jobboardState: string = CONNECT_JOBBOARD_STATES.MISSOURI.urlParam,
    action: ConnectGatedActions,
    callbackFunction: () => Promise<void>,
    school?: School | null
  ) => {
    if (!user) {
      redirectTo(connectRedirectUrls.SIGN_UP, jobboardState, action, school);
      return false;
    }

    if (user.profile.user_type !== userTypeToId.candidate) {
      return connectContextActions.setModalOpen(ConnectModals.AUTHENTICATED_ADMIN_MODAL, true);
    }

    if (!user.profile?.connect_first_profile_setup) {
      redirectTo(connectRedirectUrls.PROFILE, jobboardState, action, school);
      return false;
    }

    await callbackFunction();
  };

  return { handleConnectUserAction };
};

export const checkUserProfileCompletion = (
  user: User,
  history: History,
  jobboardState?: string,
  school?: School,
  action?: ConnectGatedActions
): boolean => {
  const formCompletionStatuses = user.preference?.nimble_connect_profile?.form_completion_statuses;
  const urlParamsToAdd = {
    [connectUrlParams.REDIRECT]: CONNECT_JOBBOARD_STATES[jobboardState].urlParam,
  };

  if (school && action) {
    urlParamsToAdd[connectUrlParams.SCHOOL] = school.nces_id;
    urlParamsToAdd[connectUrlParams.ACTION] = action;
  }

  if (!formCompletionStatuses.profile_form) {
    history.push(buildUrl(connectRedirectUrls.PROFILE, urlParamsToAdd));
    return false;
  } else if (
    !formCompletionStatuses.resume_and_credentials_form &&
    (action === ConnectGatedActions.EXPRESS_INTEREST ||
      action === ConnectGatedActions.EXPRESS_INTEREST_WAITLIST ||
      !action)
  ) {
    history.push(buildUrl(connectRedirectUrls.QUALIFICATIONS, urlParamsToAdd));
    return false;
  } else if (
    !formCompletionStatuses.preference_form &&
    (action === ConnectGatedActions.EXPRESS_INTEREST ||
      action === ConnectGatedActions.EXPRESS_INTEREST_WAITLIST ||
      !action)
  ) {
    history.push(buildUrl(connectRedirectUrls.PREFERENCES, urlParamsToAdd));
    return false;
  } else if (
    action === ConnectGatedActions.EXPRESS_INTEREST ||
    action === ConnectGatedActions.EXPRESS_INTEREST_WAITLIST
  ) {
    history.push(buildUrl(connectRedirectUrls.PROFILE, urlParamsToAdd));
    return false;
  } else {
    history.push(buildUrl(connectRedirectUrls.RETURN_TO, urlParamsToAdd));
    return true;
  }
};

export const getInitialUSVisaEligibility = (user: any): boolean => {
  return user.profile.is_eligible_to_work_in_us === null
    ? true
    : user.profile.is_eligible_to_work_in_us;
};

const calculateDaysLeft = (targetDate: string | Date): number => {
  const target = new Date(targetDate);
  const now = new Date();

  const targetTime = target.setHours(0, 0, 0, 0);
  const nowTime = now.setHours(0, 0, 0, 0);

  const timeDifference = targetTime - nowTime;
  const daysLeft = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

  return daysLeft;
};

export const DaysLeftCounter: React.FC<{ targetDate: string }> = ({ targetDate }) => {
  const daysLeft = calculateDaysLeft(targetDate);

  if (daysLeft <= -29) {
    return null;
  }

  if (daysLeft <= 0) {
    return (
      <Box display={'flex'} flexDirection={'row'} gap={1} alignItems={'center'}>
        <Typography
          sx={{
            fontSize: nimbleTheme.typography.h3.fontSize,
            color: nimbleTheme.palette.white.main,
            fontWeight: nimbleTheme.typography.fontWeightMedium,
          }}
        >
          Matching has started! 🎉
        </Typography>
        {'    '}
        <Link
          href={null}
          data-testid={ConnectProfileFlowDataTestIds.LEARN_MORE_LINK}
          sx={{
            color: nimbleTheme.palette.white.main,
            textDecoration: 'underline',
            textUnderlineOffset: '2px',
            fontSize: nimbleTheme.typography.h3.fontSize,
            fontWeight: nimbleTheme.typography.fontWeightRegular,
            letterSpacing: '-0.01em',
            '&:hover': {
              color: nimbleTheme.palette.white.main,
              textDecoration: 'underline',
              cursor: 'pointer',
              fontWeight: nimbleTheme.typography.fontWeightMediumBold,
              letterSpacing: '-0.03em',
            },
          }}
        >
          Learn More
        </Link>
      </Box>
    );
  }

  if (daysLeft > 0) {
    return (
      <div>
        <strong>{daysLeft}</strong> days until matching starts!
      </div>
    );
  }

  return <div>Matching has started! 🎉</div>;
};

export const getBannerContent = (): React.ReactElement | null => {
  return <DaysLeftCounter targetDate="2025-02-01" />;
};

export function getPlaceholderText(
  items: Option[],
  type: string,
  prefixText: 'Select' | 'Search' = 'Select'
): string {
  const itemCount = items.length;

  if (itemCount >= 2) {
    return `${type}s`;
  }

  if (itemCount === 1) {
    return '';
  }

  return `${prefixText} ${type.toLowerCase()}(s)...`;
}

export const validateLocation = async (
  userLocation: string,
  distanceFromSchool: string
): Promise<string | null> => {
  const response = await GeolocationAPI.getMapUrl({
    address: userLocation,
    radius: distanceFromSchool,
  });

  return Array.isArray(response) && response[0] !== 200
    ? 'Please provide a valid address or zip code.'
    : null;
};

export const formatDropdownOptionTestId = (
  baseTestId: string,
  option: { label: string; value: any }
): string => {
  return `${baseTestId}-${option.label.toLowerCase().replace(/\s+/g, '-')}-option`;
};
